import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  UserPlus,
  RefreshCw,
  Users,
  Shield,
  UserCheck,
  UserX,
  Edit2,
  Key,
  Check,
  X,
  AlertCircle,
  Search
} from 'lucide-react'
import { AdminService } from '../../services/adminService'

const UserManagement = () => {
  const navigate = useNavigate()
  const [users, setUsers] = useState([])
  const [allUsers, setAllUsers] = useState([]) // Store all users for client-side filtering
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  // Add user form state
  const [showAddForm, setShowAddForm] = useState(false)
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    role: 'pharmacist',
    pharmacistType: 'full_time',
    respondIoId: '',
    greetingMessage: ''
  })
  const [addingUser, setAddingUser] = useState(false)

  // Edit user profile state
  const [editingUser, setEditingUser] = useState(null)
  const [editUserData, setEditUserData] = useState({
    role: 'pharmacist',
    email: '',
    pharmacistType: 'full_time',
    respondIoId: '',
    greetingMessage: ''
  })
  const [savingUserProfile, setSavingUserProfile] = useState(false)

  // Reset password state
  const [resettingPassword, setResettingPassword] = useState(null)
  const [newPassword, setNewPassword] = useState('')
  const [confirmNewPassword, setConfirmNewPassword] = useState('')

  // Check if current user is super admin
  const currentUser = AdminService.getCurrentUser()
  const isSuperAdmin = currentUser?.role === 'super_admin'

  // Load all users (no filters - client-side search only)
  const loadUsers = async () => {
    setLoading(true)
    setError('')

    try {
      const result = await AdminService.getAllAdminUsers()
      if (result.success) {
        const loadedUsers = result.data || []
        setAllUsers(loadedUsers)
        setUsers(loadedUsers)
      } else {
        setError(result.error || 'Failed to load users')
      }
    } catch (err) {
      setError('An error occurred while loading users')
      console.error('Load users error:', err)
    } finally {
      setLoading(false)
    }
  }

  // Client-side search filter
  useEffect(() => {
    if (!searchTerm.trim()) {
      setUsers(allUsers)
    } else {
      const search = searchTerm.toLowerCase()
      const filtered = allUsers.filter(user =>
        user.username.toLowerCase().includes(search) ||
        (user.email && user.email.toLowerCase().includes(search)) ||
        user.id.toLowerCase().includes(search)
      )
      setUsers(filtered)
    }
  }, [searchTerm, allUsers])

  // Add new user
  const handleAddUser = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    // Validation
    if (!newUser.username.trim()) {
      setError('Username is required')
      return
    }
    if (!newUser.email.trim()) {
      setError('Email is required')
      return
    }
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailPattern.test(newUser.email.trim())) {
      setError('Please enter a valid email address')
      return
    }

    setAddingUser(true)

    try {
      const result = await AdminService.createAdminUser(
        newUser.username.trim(),
        newUser.email.trim(),
        newUser.role,
        newUser.respondIoId,
        newUser.greetingMessage,
        newUser.pharmacistType
      )

      if (result.success) {
        setSuccess(`Invite sent to "${newUser.email}" and user "${newUser.username}" created`)
        setNewUser({ username: '', email: '', role: 'pharmacist', pharmacistType: 'full_time', respondIoId: '', greetingMessage: '' })
        setShowAddForm(false)
        loadUsers()
      } else {
        setError(result.error || 'Failed to create user')
      }
    } catch (err) {
      setError('An error occurred while creating user')
      console.error('Add user error:', err)
    } finally {
      setAddingUser(false)
    }
  }

  // Toggle user active status
  const handleToggleActive = async (userId, currentStatus) => {
    setError('')
    setSuccess('')

    const user = users.find(u => u.id === userId)
    const action = currentStatus ? 'deactivate' : 'activate'

    if (!window.confirm(`Are you sure you want to ${action} user "${user?.username}"?`)) {
      return
    }

    try {
      const result = await AdminService.updateAdminUser(userId, { is_active: !currentStatus })

      if (result.success) {
        setSuccess(`User "${user?.username}" ${action}d successfully`)
        loadUsers()
      } else {
        setError(result.error || `Failed to ${action} user`)
      }
    } catch (err) {
      setError(`An error occurred while ${action}ing user`)
      console.error('Toggle active error:', err)
    }
  }

  // Open edit user modal
  const openEditUserModal = (user) => {
    setError('')
    setEditingUser(user)
    setEditUserData({
      role: user.role || 'pharmacist',
      email: user.email || '',
      pharmacistType: user.pharmacistType || 'full_time',
      respondIoId: user.respondIoId || '',
      greetingMessage: user.greetingMessage || ''
    })
  }

  // Close edit user modal
  const closeEditUserModal = () => {
    setEditingUser(null)
    setEditUserData({
      role: 'pharmacist',
      email: '',
      pharmacistType: 'full_time',
      respondIoId: '',
      greetingMessage: ''
    })
    setSavingUserProfile(false)
  }

  // Save user profile updates
  const handleSaveUserProfile = async () => {
    setError('')
    setSuccess('')

    if (!editingUser?.id) {
      setError('No user selected for update')
      return
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const normalizedEmail = editUserData.email.trim().toLowerCase()
    if (!normalizedEmail) {
      setError('Email is required')
      return
    }
    if (!emailPattern.test(normalizedEmail)) {
      setError('Please enter a valid email address')
      return
    }

    setSavingUserProfile(true)

    try {
      const result = await AdminService.updateAdminUserProfile(editingUser.id, {
        role: editUserData.role,
        email: normalizedEmail,
        respondIoId: editUserData.respondIoId,
        greetingMessage: editUserData.greetingMessage,
        pharmacistType: editUserData.pharmacistType
      })

      if (result.success) {
        setSuccess(`User profile updated for "${editingUser.username}"`)
        closeEditUserModal()
        await loadUsers()
      } else {
        setError(result.error || 'Failed to update user profile')
      }
    } catch (err) {
      setError('An error occurred while updating user profile')
      console.error('Update user profile error:', err)
    } finally {
      setSavingUserProfile(false)
    }
  }

  // Reset password
  const handleResetPassword = async (userId) => {
    setError('')
    setSuccess('')

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters')
      return
    }
    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match')
      return
    }

    const user = users.find(u => u.id === userId)

    try {
      const result = await AdminService.resetUserPassword(userId, newPassword)

      if (result.success) {
        setSuccess(`Password reset for "${user?.username}"`)
        setResettingPassword(null)
        setNewPassword('')
        setConfirmNewPassword('')
      } else {
        setError(result.error || 'Failed to reset password')
      }
    } catch (err) {
      setError('An error occurred while resetting password')
      console.error('Reset password error:', err)
    }
  }

  // Get role badge styling
  const getRoleBadge = (role) => {
    switch (role) {
      case 'super_admin':
        return 'bg-purple-100 text-purple-800'
      case 'coordinator':
        return 'bg-teal-100 text-teal-800'
      case 'pharmacist':
        return 'bg-blue-100 text-blue-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getRoleLabel = (role) => {
    switch (role) {
      case 'super_admin':
        return 'Super Admin'
      case 'coordinator':
        return 'Coordinator'
      case 'pharmacist':
        return 'Pharmacist'
      default:
        return role
    }
  }

  // Load data on mount
  useEffect(() => {
    if (!isSuperAdmin) {
      navigate('/admin')
      return
    }
    loadUsers()
  }, [isSuperAdmin, navigate])

  // Auto-dismiss success message
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 5000)
      return () => clearTimeout(timer)
    }
  }, [success])

  if (!isSuperAdmin) {
    return null
  }

  return (
    <div className="min-h-screen bg-gray-50" dir="ltr">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/admin')}
                className="mr-4 p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
              <span className="ml-3 px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                Super Admin
              </span>
            </div>

            <div className="flex items-center space-x-4">
              <button
                onClick={loadUsers}
                disabled={loading}
                className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>

              <button
                onClick={() => setShowAddForm(true)}
                className="flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-md transition-colors"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Invite User
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-6xl">

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by username, email, or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          {searchTerm && (
            <p className="text-sm text-gray-500 mt-2">
              Showing {users.length} of {allUsers.length} users
            </p>
          )}
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-red-600">{error}</p>
            <button
              onClick={() => setError('')}
              className="ml-auto text-red-400 hover:text-red-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
            <Check className="w-5 h-5 text-green-600 mr-2" />
            <p className="text-green-600">{success}</p>
          </div>
        )}

        {/* Add User Form Modal */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="p-6 border-b">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <UserPlus className="w-5 h-5 mr-2 text-purple-600" />
                  Invite New User
                </h2>
              </div>

              <form onSubmit={handleAddUser} className="p-6 space-y-4 text-left">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    Username
                  </label>
                  <input
                    type="text"
                    value={newUser.username}
                    onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Enter username"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    Email
                  </label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Enter email address"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    Role
                  </label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="pharmacist">Pharmacist</option>
                    <option value="coordinator">Coordinator</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500 text-left">
                    {newUser.role === 'pharmacist' && 'Can view assigned consultations and set outcomes'}
                    {newUser.role === 'coordinator' && 'Can view all consultations (read-only) and toggle staff availability'}
                    {newUser.role === 'super_admin' && 'Full access to all features'}
                  </p>
                </div>

                {newUser.role === 'pharmacist' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                      Pharmacist Type
                    </label>
                    <select
                      value={newUser.pharmacistType}
                      onChange={(e) => setNewUser({ ...newUser, pharmacistType: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="full_time">Full-time</option>
                      <option value="freelance">Freelance</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500 text-left">
                      Freelance receives live/new consultations only after choosing a daily amount.
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    Respond.io ID
                  </label>
                  <input
                    type="text"
                    value={newUser.respondIoId}
                    onChange={(e) => setNewUser({ ...newUser, respondIoId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Enter Respond.io ID"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    Greeting Message
                  </label>
                  <textarea
                    value={newUser.greetingMessage}
                    onChange={(e) => setNewUser({ ...newUser, greetingMessage: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Enter greeting message"
                    rows={3}
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddForm(false)
                      setNewUser({ username: '', email: '', role: 'pharmacist', pharmacistType: 'full_time', respondIoId: '', greetingMessage: '' })
                      setError('')
                    }}
                    className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={addingUser}
                    className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md disabled:opacity-50"
                  >
                    {addingUser ? 'Sending...' : 'Send Invite'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <Users className="w-5 h-5 mr-2 text-gray-600" />
                Admin Users
              </h2>
              <span className="text-sm text-gray-500">
                {users.length} user{users.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading users...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center">
              <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-600">No users found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Username
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} className={`hover:bg-gray-50 ${!user.is_active ? 'opacity-60' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${user.is_active ? 'bg-purple-100' : 'bg-gray-100'
                            }`}>
                            <Shield className={`w-4 h-4 ${user.is_active ? 'text-purple-600' : 'text-gray-400'}`} />
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-gray-900">{user.username}</p>
                            {user.id === currentUser?.id && (
                              <span className="text-xs text-gray-500">(You)</span>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${getRoleBadge(user.role)}`}>
                          {getRoleLabel(user.role)}
                        </span>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {user.is_active ? (
                          <span className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                            <UserCheck className="w-3 h-3 mr-1" />
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
                            <UserX className="w-3 h-3 mr-1" />
                            Inactive
                          </span>
                        )}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(user.created_at).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            onClick={() => openEditUserModal(user)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title="Edit user"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>

                          {/* Reset Password */}
                          <button
                            onClick={() => {
                              setResettingPassword(user.id)
                              setNewPassword('')
                              setConfirmNewPassword('')
                            }}
                            className="p-1.5 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded transition-colors"
                            title="Reset password"
                          >
                            <Key className="w-4 h-4" />
                          </button>

                          {/* Toggle Active - disabled for own account */}
                          {user.id !== currentUser?.id && (
                            <button
                              onClick={() => handleToggleActive(user.id, user.is_active)}
                              className={`p-1.5 rounded transition-colors ${user.is_active
                                ? 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                                : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                                }`}
                              title={user.is_active ? 'Deactivate user' : 'Activate user'}
                            >
                              {user.is_active ? (
                                <UserX className="w-4 h-4" />
                              ) : (
                                <UserCheck className="w-4 h-4" />
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Edit User Modal */}
        {editingUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="p-6 border-b">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Edit2 className="w-5 h-5 mr-2 text-blue-600" />
                  Edit User
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  User: {editingUser.username}
                </p>
              </div>

              <div className="p-6 space-y-4 text-left">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    Email
                  </label>
                  <input
                    type="email"
                    value={editUserData.email}
                    onChange={(e) => setEditUserData({ ...editUserData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter email address"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    Role
                  </label>
                  <select
                    value={editUserData.role}
                    onChange={(e) => setEditUserData({ ...editUserData, role: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="pharmacist">Pharmacist</option>
                    <option value="coordinator">Coordinator</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>

                {editUserData.role === 'pharmacist' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                      Pharmacist Type
                    </label>
                    <select
                      value={editUserData.pharmacistType}
                      onChange={(e) => setEditUserData({ ...editUserData, pharmacistType: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="full_time">Full-time</option>
                      <option value="freelance">Freelance</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500 text-left">
                      Full-time keeps hourly limit and backlog dispatch. Freelance is live/new only.
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    Respond.io ID
                  </label>
                  <input
                    type="text"
                    value={editUserData.respondIoId}
                    onChange={(e) => setEditUserData({ ...editUserData, respondIoId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter Respond.io ID"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    Greeting Message
                  </label>
                  <textarea
                    value={editUserData.greetingMessage}
                    onChange={(e) => setEditUserData({ ...editUserData, greetingMessage: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter greeting message"
                    rows={3}
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      closeEditUserModal()
                      setError('')
                    }}
                    disabled={savingUserProfile}
                    className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveUserProfile}
                    disabled={savingUserProfile}
                    className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50"
                  >
                    {savingUserProfile ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reset Password Modal */}
        {resettingPassword && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="p-6 border-b">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Key className="w-5 h-5 mr-2 text-orange-600" />
                  Reset Password
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  For user: {users.find(u => u.id === resettingPassword)?.username}
                </p>
              </div>

              <div className="p-6 space-y-4 text-left">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Enter new password (min 6 characters)"
                    minLength={6}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Confirm new password"
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setResettingPassword(null)
                      setNewPassword('')
                      setConfirmNewPassword('')
                      setError('')
                    }}
                    className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleResetPassword(resettingPassword)}
                    className="flex-1 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-md"
                  >
                    Reset Password
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Role Legend */}
        <div className="mt-6 p-4 bg-white rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Role Permissions</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-start space-x-2">
              <span className={`px-2 py-1 text-xs rounded-full ${getRoleBadge('super_admin')}`}>
                Super Admin
              </span>
              <p className="text-gray-600 text-xs mt-0.5">
                Full access - view all data, manage users, export CSV, control staff availability
              </p>
            </div>
            <div className="flex items-start space-x-2">
              <span className={`px-2 py-1 text-xs rounded-full ${getRoleBadge('coordinator')}`}>
                Coordinator
              </span>
              <p className="text-gray-600 text-xs mt-0.5">
                Read-only view of all consultations, can toggle staff online/offline status
              </p>
            </div>
            <div className="flex items-start space-x-2">
              <span className={`px-2 py-1 text-xs rounded-full ${getRoleBadge('pharmacist')}`}>
                Pharmacist
              </span>
              <p className="text-gray-600 text-xs mt-0.5">
                View assigned consultations only, set outcomes for their consultations
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}

export default UserManagement


